import {
    companyLocationSchema,
    postalCodesByCantonSchema,
    postalCodesInRangeSchema,
    workRegionSelectionSchema,
    type PostalCodeOption,
} from '@houzy/schemas/partner-cockpit/company';
import {
    keepPreviousData,
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';

export async function fetchPostalCodesByCanton(signal?: AbortSignal): Promise<Record<string, PostalCodeOption[]>> {
    return apiRequest(partnerCockpitApiPath('company/work-regions/postal-codes-by-canton'), {
        signal,
        responseSchema: postalCodesByCantonSchema,
    });
}

export const postalCodesByCantonQueryOptions = queryOptions({
    queryKey: ['settings', 'postalCodesByCanton'] as const,
    queryFn: ({ signal }) => fetchPostalCodesByCanton(signal),
    staleTime: STALE_TIME_MS,
});

export function usePostalCodesByCanton(): UseQueryResult<Record<string, PostalCodeOption[]>> {
    return useQuery(postalCodesByCantonQueryOptions);
}

type PostalCodesInRangeQueryKey = readonly ['settings', 'postalCodesInRange', number];

async function fetchPostalCodesInRange(distanceKm: number, signal?: AbortSignal): Promise<string[]> {
    const result = await apiRequest(
        partnerCockpitApiPath(`company/work-regions/postal-codes-in-range?rangeMax=${distanceKm}`),
        {
            signal,
            responseSchema: postalCodesInRangeSchema,
        },
    );
    return result.postalCodes;
}

function postalCodesInRangeQueryOptions(
    distanceKm: number,
): UseQueryOptions<string[], Error, string[], PostalCodesInRangeQueryKey> {
    return queryOptions({
        queryKey: ['settings', 'postalCodesInRange', distanceKm] as const,
        queryFn: ({ signal }) => fetchPostalCodesInRange(distanceKm, signal),
        enabled: distanceKm > 0,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
}

export function usePostalCodesInRange(distanceKm: number): UseQueryResult<string[]> {
    return useQuery(postalCodesInRangeQueryOptions(distanceKm));
}

export type CompanyLocation = { lat: number; lng: number };

// Absence stays `null`: react-query rejects `undefined` as query data, so a partner without a geocoded address
// would fail the query instead of resolving to "no location".
async function fetchCompanyLocation(signal?: AbortSignal): Promise<CompanyLocation | null> {
    const { position } = await apiRequest(partnerCockpitApiPath('company/work-regions/company-location'), {
        signal,
        responseSchema: companyLocationSchema,
    });
    return position;
}

export const companyLocationQueryOptions = queryOptions({
    queryKey: ['settings', 'companyLocation'] as const,
    queryFn: ({ signal }) => fetchCompanyLocation(signal),
    staleTime: STALE_TIME_MS,
});

export function useCompanyLocation(): UseQueryResult<CompanyLocation | null> {
    return useQuery(companyLocationQueryOptions);
}

type WorkRegionSelection = { postalCodes: ReadonlySet<string>; cantonIds: ReadonlySet<string> };

async function fetchWorkRegionSelection(signal?: AbortSignal): Promise<WorkRegionSelection> {
    const { postalCodes, cantonIds } = await apiRequest(
        partnerCockpitApiPath('company/work-regions/work-region-selection'),
        { signal, responseSchema: workRegionSelectionSchema },
    );
    return { postalCodes: new Set(postalCodes), cantonIds: new Set(cantonIds) };
}

export const workRegionSelectionQueryKey = ['settings', 'workRegionSelection'] as const;

export const workRegionSelectionQueryOptions = queryOptions({
    queryKey: workRegionSelectionQueryKey,
    queryFn: ({ signal }) => fetchWorkRegionSelection(signal),
    staleTime: STALE_TIME_MS,
});

export function useWorkRegionSelection(): UseQueryResult<WorkRegionSelection> {
    return useQuery(workRegionSelectionQueryOptions);
}

export function useSaveWorkRegionSelection(): UseMutationResult<void, Error, ReadonlySet<string>> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['settings', 'save', 'workRegionSelection'],
        mutationFn: (postalCodes) =>
            apiRequest(partnerCockpitApiPath('company/work-regions/work-region-selection'), {
                method: 'PUT',
                body: { postalCodes: Array.from(postalCodes) },
            }),
        onSuccess: (_, postalCodes) => {
            queryClient.setQueryData<WorkRegionSelection>(workRegionSelectionQueryKey, (prev) =>
                prev ? { ...prev, postalCodes } : undefined,
            );
        },
    });
}
